import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sprout, Leaf, Flower2, Heart } from "lucide-react";

const ACCENT = "#7BC96F";
const QUALITY_META = {
  patience: { color: "#2DD4BF", icon: Leaf },
  generosity: { color: "#F5C842", icon: Flower2 },
  honesty: { color: "#A78BFA", icon: Sprout },
  compassion: { color: "#FB7185", icon: Heart },
};

export const KarmaGarden = () => {
  const [garden, setGarden] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const loadGarden = () => api.get("/games/karma/garden").then((r) => setGarden(r.data.garden)).catch(() => {});
  useEffect(() => { loadGarden(); }, []);

  const encounter = async () => {
    setLoading(true); setResult(null); setScenario(null);
    try {
      const { data } = await api.post("/games/karma/scenario");
      setScenario(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  const choose = async (i) => {
    navigator.vibrate?.(8);
    try {
      const { data } = await api.post("/games/karma/choose", { scenario_id: scenario.scenario_id, option_index: i });
      setResult(data);
      setGarden(data.garden);
      setScenario(null);
      toast.success(`+${data.xp_gained} XP`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="karma-garden">
      <div className="flex items-center gap-2 mb-4 text-sm" style={{ color: ACCENT }}><Sprout className="w-4 h-4" /> Tend your garden through everyday choices</div>

      {garden && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="garden-state">
          {Object.entries(QUALITY_META).map(([q, meta]) => {
            const Icon = meta.icon;
            const val = garden.qualities?.[q] || 0;
            return (
              <div key={q} className="rounded-xl border border-white/10 p-4 bg-white/[0.02]">
                <Icon className="w-6 h-6 mb-2" style={{ color: meta.color }} />
                <p className="text-xs text-slate-400 capitalize">{q}</p>
                <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, val * 12)}%`, background: meta.color }} />
                </div>
                <p className="text-xs text-slate-500 mt-1" data-testid={`garden-${q}`}>{val}</p>
              </div>
            );
          })}
        </div>
      )}
      {garden && (
        <p className="text-xs text-slate-500 mb-6">Choices made: {garden.choices} · habits noticed (weeds): {garden.weeds}</p>
      )}

      {result && (
        <div className="rounded-xl border p-5 mb-6" style={{ borderColor: `${ACCENT}40`, background: `${ACCENT}0d` }} data-testid="karma-result">
          <p className="text-sm text-slate-200 mb-3">{result.consequence}</p>
          {result.reflection && <p className="text-sm text-slate-400 italic">Reflect: {result.reflection}</p>}
        </div>
      )}

      {scenario ? (
        <div data-testid="karma-scenario">
          <p className="text-lg text-slate-50 font-medium mb-4">{scenario.situation}</p>
          <div className="grid gap-2">
            {scenario.options.map((o, i) => (
              <button key={i} data-testid={`karma-opt-${i}`} onClick={() => choose(i)}
                className="text-left rounded-xl border border-white/10 px-4 py-3 text-sm text-slate-300 hover:border-white/40 hover:bg-white/[0.03] transition-colors duration-300">
                {o.text}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <button data-testid="karma-encounter-btn" onClick={encounter} disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-medium text-slate-900 disabled:opacity-60" style={{ background: ACCENT }}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sprout className="w-4 h-4" />} {result ? "Next moment" : "Encounter a moment"}
        </button>
      )}
    </div>
  );
};
